import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Typography } from '@mui/material'
import { IconChevronRight, IconUsers } from '@tabler/icons-react'
import { ROUTES } from '../../../../helpers/routes-helper'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { logEvent } from '../../../../analytics/analytics'

/**
 * Home entry point into the anon-friendly Group Dining Session flow ("Calendly for dinner").
 * Unlike the join path (a shared /dine/:token link) and the ChatGPT MCP tool, this is the
 * discoverable in-app way to *start* a session. Anon-friendly — no login gate; it routes straight
 * to /dine (PlannerLanding). Renders on web and mobile (web has no bottom-nav Dine tab, so
 * this is the only web entry). Companion surface: the mobile bottom-nav Dine tab.
 */
const GroupDiningCTA: React.FC = () => {
  const { navigate, theme } = useAppUtilities()
  const { isAuthenticated } = useAuth0()

  const handleClick = (): void => {
    logEvent('session_entry_clicked', {
      entry_point: 'home_cta',
      is_logged_in: isAuthenticated,
      screen_name: 'home',
    })
    navigate(ROUTES.DINE)
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        paddingLeft: '12px', // Reduced for accent bar
        marginTop: '16px',
        marginBottom: '16px',
        marginX: 0,
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
        '&:active': {
          backgroundColor: theme.palette.grey[50],
          borderColor: theme.palette.grey[300],
        },
        '&:hover': {
          borderColor: theme.palette.grey[300],
          filter: 'drop-shadow(0px 2px 4px 0px #6666664D)',
        },
      }}
    >
      {/* Left: Icon + Title + Subtext */}
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: theme.palette.secondary[50],
            marginRight: '12px',
            flexShrink: 0,
          }}
        >
          <IconUsers size={22} stroke={2} color={theme.palette.secondary.main} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: theme.palette.grey[900],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Plan a group dinner
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.grey[600],
              marginTop: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Share a link — everyone picks, we find the spot
          </Typography>
        </Box>
      </Box>

      {/* Right: Chevron Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '12px',
          flexShrink: 0,
        }}
      >
        <IconChevronRight size={20} stroke={2} color={theme.palette.grey[600]} />
      </Box>
    </Box>
  )
}

export default GroupDiningCTA
